/**
 * Development overlay, enabled with ?dev=1.
 *
 * Reports the things that silently rot in a browser game: frame time, draw
 * calls, triangle count, and — critically — JS heap growth. A non-flat heap
 * during steady-state gameplay means per-frame allocation crept in somewhere,
 * which is the leading cause of intermittent stutter and is otherwise almost
 * impossible to notice by feel until it is severe.
 */
export class DevOverlay {
  constructor(ctx) {
    this.ctx = ctx;
    this.enabled = !!ctx.config.dev;
    if (!this.enabled) return;

    const el = document.createElement('div');
    el.id = 'dev-overlay';
    el.style.cssText = `
      position: fixed; top: 8px; left: 8px; z-index: 40;
      font: 11px/1.45 ui-monospace, Menlo, monospace;
      color: #9fe8ff; background: rgba(4,10,18,.78);
      border: 1px solid rgba(80,150,210,.28); border-radius: 4px;
      padding: 7px 10px; pointer-events: none; white-space: pre;
      text-shadow: 0 0 6px rgba(0,0,0,.9); min-width: 216px;
    `;
    document.body.appendChild(el);
    this.el = el;

    const cv = document.createElement('canvas');
    cv.width = 216; cv.height = 40;
    cv.style.cssText = `
      position: fixed; top: 8px; left: 232px; z-index: 40;
      background: rgba(4,10,18,.78);
      border: 1px solid rgba(80,150,210,.28); border-radius: 4px;
      pointer-events: none;
    `;
    document.body.appendChild(cv);
    this.canvas = cv;
    this.g = cv.getContext('2d');

    this.history = new Float32Array(216);
    this.hi = 0;

    this._acc = 0;
    this._heapPrev = 0;
    this._heapRate = 0;
    this._lines = [];
  }

  update(dt) {
    if (!this.enabled) return;
    const { ctx } = this;
    const t = ctx.time;

    // Frame-time graph. 16.6 ms is drawn as the budget line.
    this.history[this.hi] = t.rawDt * 1000;
    this.hi = (this.hi + 1) % this.history.length;

    this._acc += dt;
    if (this._acc < 0.12) { this._drawGraph(); return; }
    this._acc = 0;

    const info = ctx.peek('renderer')?.info;
    const mem = performance.memory;
    let heapLine = '';
    if (mem) {
      const mb = mem.usedJSHeapSize / 1048576;
      if (this._heapPrev > 0) {
        const d = mb - this._heapPrev;
        // Smooth: GC sawtooth makes the instantaneous rate meaningless.
        this._heapRate += (d - this._heapRate) * 0.25;
      }
      this._heapPrev = mb;
      const flag = this._heapRate > 0.35 ? ' ALLOC!' : '';
      heapLine = `heap  ${mb.toFixed(1)} MB  ${this._heapRate >= 0 ? '+' : ''}${this._heapRate.toFixed(2)}/s${flag}\n`;
    }

    const player = ctx.peek('player');
    const ai = ctx.peek('ai');
    const fx = ctx.peek('fx');

    let s = '';
    s += `${t.fps.toFixed(0)} fps   ${t.smoothedFrameMs.toFixed(2)} ms\n`;
    s += `tick  ${t.tick}   frame ${t.frame}   x${t.scale.toFixed(2)}\n`;
    if (info) {
      s += `draws ${info.render.calls}   tris ${(info.render.triangles / 1000).toFixed(1)}k\n`;
      s += `progs ${info.programs?.length ?? 0}   tex ${info.memory.textures}   geo ${info.memory.geometries}\n`;
    }
    s += heapLine;
    if (player) {
      const p = player.position, v = player.velocity;
      s += `pos   ${p.x.toFixed(1)} ${p.y.toFixed(1)} ${p.z.toFixed(1)}\n`;
      s += `spd   ${Math.hypot(v.x, v.z).toFixed(2)} m/s  vy ${v.y.toFixed(2)}\n`;
      s += `state ${player.stateName}  jumps ${player.jumpsUsed}/${ctx.config.MOVE.maxJumps}\n`;
    }
    if (ai) s += `ai    ${ai.aliveCount} alive / ${ai.totalSpawned} spawned\n`;
    if (fx) s += `fx    ${fx.particleCount} parts  ${fx.decalCount} decals\n`;

    this.el.textContent = s;
    this._drawGraph();
  }

  _drawGraph() {
    const g = this.g;
    if (!g) return;
    const w = this.canvas.width, h = this.canvas.height;
    g.clearRect(0, 0, w, h);

    // 16.6 ms budget line.
    const scale = h / 40; // 40 ms full scale
    g.strokeStyle = 'rgba(120,200,150,.45)';
    g.beginPath();
    const budgetY = h - 16.6 * scale;
    g.moveTo(0, budgetY); g.lineTo(w, budgetY); g.stroke();

    g.beginPath();
    for (let i = 0; i < w; i++) {
      const v = this.history[(this.hi + i) % this.history.length];
      const y = Math.max(0, h - Math.min(v, 40) * scale);
      if (i === 0) g.moveTo(i, y); else g.lineTo(i, y);
    }
    g.strokeStyle = '#7fd3ff';
    g.lineWidth = 1;
    g.stroke();
  }

  dispose() {
    this.el?.remove();
    this.canvas?.remove();
  }
}
